import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { GridApi } from 'ag-grid-community';

// A Studio block's table grid, published by the grid it draws (a totals,
// records, date comparison or pace Table) so its header's menu can export it. A table
// drawn by DataTable publishes its export handle instead.
interface BlockGridApi {
  gridApi?: GridApi;
  setGridApi: (gridApi: GridApi | undefined) => void;
  exportHandle?: DataTable.ExportHandle;
  setExportHandle: (handle: DataTable.ExportHandle | undefined) => void;
}

const BlockGridApiContext = React.createContext<BlockGridApi>({
  setGridApi: () => undefined,
  setExportHandle: () => undefined,
});

export const BlockGridApiProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [gridApi, setGridApi] = useState<GridApi>();
  const [exportHandle, setExportHandle] = useState<DataTable.ExportHandle>();
  const value = useMemo(
    () => ({ gridApi, setGridApi, exportHandle, setExportHandle }),
    [exportHandle, gridApi],
  );
  return (
    <BlockGridApiContext.Provider value={value}>
      {children}
    </BlockGridApiContext.Provider>
  );
};

// Publishes a grid to the block around it, if there's one (elsewhere it does
// nothing), withdrawing it when the grid goes
export const usePublishBlockGridApi = (gridApi: GridApi | undefined) => {
  const { setGridApi } = useContext(BlockGridApiContext);
  useEffect(() => {
    setGridApi(gridApi);
    return () => setGridApi(undefined);
  }, [gridApi, setGridApi]);
};

export const usePublishBlockExportHandle = (
  exportHandle: DataTable.ExportHandle | undefined,
) => {
  const { setExportHandle } = useContext(BlockGridApiContext);
  useEffect(() => {
    setExportHandle(exportHandle);
  }, [exportHandle, setExportHandle]);
  useEffect(() => () => setExportHandle(undefined), [setExportHandle]);
};

export default BlockGridApiContext;
